import { useEffect } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import type { QueryResult } from '@apollo/client';

import { DispensariesForChainDocument, OrdersForConsumerInChainDocument, StringSortDirections } from 'src/gql/graphql';
import type {
  Dispensaries,
  DispensariesForChainQueryVariables,
  OrdersForConsumerInChainQuery,
  OrdersForConsumerInChainQueryVariables,
} from 'src/gql/graphql';
import useUser from 'src/hooks/use-user';
import { getLogger } from 'src/utils/datadog-logger';
import { ConsumerUser } from '../../../account.types';
import { Orders } from './types';

const logger = getLogger('use-orders-for-consumer');

type UseDispensariesForChainParams = {
  chain?: string | null;
};

type UseOrdersForConsumerParams = {
  dispensary: Dispensaries | null;
  skip?: boolean;
  currentLocationOnly?: boolean;
};

type UseVariablesForOrdersForConsumerParams = {
  dispensary: Dispensaries | null;
  user: ConsumerUser;
  dispensaryIdsForChain: string[] | [];
};

export type UseOrdersForConsumerReturn = {
  loading: boolean;
  fetchMore: QueryResult<OrdersForConsumerInChainQuery, OrdersForConsumerInChainQueryVariables>['fetchMore'];
  totalCount: number;
  orders: Orders;
  hasError: boolean;
  refetch: () => void;
};

function useVariablesForChainDispensaries({
  chain,
}: UseDispensariesForChainParams): DispensariesForChainQueryVariables | undefined {
  if (!chain) {
    return undefined;
  }

  return {
    dispensaryFilter: {
      type: `Dispensary`,
      chain,
      activeOnly: true,
      includeStealthMode: true,
    },
  };
}

function useDispensariesForChainIds({ chain }: UseDispensariesForChainParams): string[] | [] {
  const variables = useVariablesForChainDispensaries({ chain });

  const { data, error } = useQuery(DispensariesForChainDocument, {
    variables,
    skip: !variables,
  });

  useEffect(() => {
    if (error) {
      logger.error('Failed to fetch chain dispensaries for order history', { chain, errorMessage: error.message });
    }
  }, [error, chain]);

  const dispoIds = data?.filteredDispensaries?.map((dispo) => dispo?.id ?? '').filter((id) => id !== '') ?? [];

  return dispoIds;
}

function useVariablesForOrdersForConsumer({
  dispensary,
  user,
  dispensaryIdsForChain,
}: UseVariablesForOrdersForConsumerParams): OrdersForConsumerInChainQueryVariables {
  const userId = user.id;
  const dispensaryId = dispensary?.id;

  return {
    ordersSort: { sortBy: `createdAt`, sortDirection: StringSortDirections.desc },
    ordersFilter: {
      userId,
      dispensaryId,
      dispensaryIds: [...dispensaryIdsForChain],
    },
    ordersPagination: { limit: 5 },
  };
}

export function useOrdersForConsumer({
  dispensary,
  skip: externalSkip = false,
  currentLocationOnly = false,
}: UseOrdersForConsumerParams): UseOrdersForConsumerReturn {
  const user = useUser();
  const dispensaryIdsForChain = useDispensariesForChainIds({ chain: currentLocationOnly ? null : dispensary?.chain });
  const variables = useVariablesForOrdersForConsumer({ dispensary, user, dispensaryIdsForChain });
  const skip = externalSkip || !variables.ordersFilter.userId;

  const { data, loading, error, networkStatus, fetchMore, refetch } = useQuery(OrdersForConsumerInChainDocument, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip,
  });

  useEffect(() => {
    if (error) {
      logger.error('Failed to fetch online order history', { errorMessage: error.message });
    }
  }, [error]);

  const isLoading = skip || loading || networkStatus === NetworkStatus.fetchMore;
  const totalCount = data?.filteredOrders?.queryInfo?.totalCount ?? 0;
  const orders =
    data?.filteredOrders?.orders && Array.isArray(data.filteredOrders.orders) ? data.filteredOrders.orders : [];

  const handleRefetchOrders = (): void => {
    void refetch().catch(() => undefined);
  };

  return {
    fetchMore,
    loading: isLoading,
    orders,
    totalCount,
    hasError: !!error,
    refetch: handleRefetchOrders,
  };
}

export const queryMoreOrders = (fetchMore: UseOrdersForConsumerReturn['fetchMore'], orders: Orders): void => {
  void fetchMore({
    variables: {
      ordersPagination: {
        limit: 5,
        offset: orders.length,
      },
    },
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult?.filteredOrders) {
        return prev;
      }

      /* eslint-disable @typescript-eslint/naming-convention */
      const { __typename, queryInfo } = fetchMoreResult.filteredOrders;
      const prevOrders = prev.filteredOrders?.orders ?? [];
      const newOrders = fetchMoreResult.filteredOrders.orders ?? [];

      return {
        filteredOrders: {
          __typename,
          queryInfo,
          orders: [...prevOrders, ...newOrders],
        },
      };
    },
  }).catch((err: unknown) => {
    logger.error('Failed to load more online orders', {
      errorMessage: err instanceof Error ? err.message : String(err),
    });
  });
};
